import type { components } from "./schema";

export type Schemas = components["schemas"];
export type Me = Schemas["Me"];
export type User = Schemas["User"];
export type UserPage = Schemas["UserPage"];
export type UserCreate = Schemas["UserCreate"];
export type UserUpdate = Schemas["UserUpdate"];
export type Role = Schemas["Role"];
export type RoleCreate = Schemas["RoleCreate"];
export type RoleUpdate = Schemas["RoleUpdate"];
export type Permission = Schemas["Permission"];
export type AuditEvent = Schemas["AuditEvent"];
export type AuditPage = Schemas["AuditPage"];
export type Batch = Schemas["Batch"];
export type BatchPage = Schemas["BatchPage"];
export type BatchReport = Schemas["BatchReport"];
export type CertificatePreview = Schemas["CertificatePreview"];
export type BulkBatchDecisionRequest = Schemas["BulkBatchDecisionRequest"];
export type BulkBatchDecisionResponse = Schemas["BulkBatchDecisionResponse"];
export type ErrorEnvelope = Schemas["ErrorEnvelope"];
export type Certificate = Schemas["Certificate"];
export type CertificatePage = Schemas["CertificatePage"];
export type CertificateDetail = Schemas["CertificateDetail"];
export type CertificateVerifications = Schemas["CertificateVerifications"];
export type RevocationQueueItem = Schemas["RevocationQueueItem"];
export type RevocationPage = Schemas["RevocationPage"];
export type ReissueRequest = Schemas["ReissueRequest"];
export type ReissueRequestCreate = Schemas["ReissueRequestCreate"];
export type ReissueRequestList = Schemas["ReissueRequestList"];
export type ReissueQueueItem = Schemas["ReissueQueueItem"];
export type ReissueQueuePage = Schemas["ReissueQueuePage"];
export type ReissueApproveResult = Schemas["ReissueApproveResult"];
export type MemberSummary = Schemas["MemberSummary"];
export type MemberPage = Schemas["MemberPage"];
export type MemberDetail = Schemas["MemberDetail"];
export type MemberUpdate = Schemas["MemberUpdate"];
export type MemberImportSummary = Schemas["MemberImportSummary"];
export type Payment = Schemas["Payment"];
export type PaymentPage = Schemas["PaymentPage"];
export type PaymentCreate = Schemas["PaymentCreate"];
export type BatchPaymentStatus = Schemas["BatchPaymentStatus"];
export type AnalyticsSummary = Schemas["AnalyticsSummary"];
export type AnalyticsMetrics = Schemas["AnalyticsMetrics"];
export type AnalyticsPaymentTotal = Schemas["AnalyticsPaymentTotal"];
export type Program = Schemas["Program"];
export type ProgramCreate = Schemas["ProgramCreate"];
/** Send only what changes; `null` clears `induction_date` or `template_id`. */
export type ProgramUpdate = Schemas["ProgramUpdate"];
export type Template = Schemas["Template"];
export type TemplateDetail = Schemas["TemplateDetail"];
export type TemplateVersion = Schemas["TemplateVersion"];
export type TemplateVersionDetail = Schemas["TemplateVersionDetail"];
export type TemplateDefinition = Schemas["TemplateDefinition"];
export type Font = Schemas["Font"];
export type FontStyle = Font["style"];
export type TemplateAsset = Schemas["TemplateAsset"];
export type Signature = Schemas["Signature"];
export type SignatureVersion = Schemas["SignatureVersion"];
export type PrinterProfile = Schemas["PrinterProfile"];
export type PrinterProfileCreate = Schemas["PrinterProfileCreate"];
export type PrinterProfileUpdate = Schemas["PrinterProfileUpdate"];
export type PrinterTestResult = Schemas["PrinterTestResult"];
export type DistributePrintRequest = Schemas["DistributePrintRequest"];
export type PrintRunCreated = Schemas["PrintRunCreated"];
export type PrintRun = Schemas["PrintRun"];
export type PrintSpool = Schemas["PrintSpool"];
export type SpoolOutcome = Schemas["SpoolOutcomeRequest"]["outcome"];
export type EmailTemplate = Schemas["EmailTemplate"];
export type EmailTemplateCreate = Schemas["EmailTemplateCreate"];
export type EmailTemplateUpdate = Schemas["EmailTemplateUpdate"];
export type EmailPreview = Schemas["EmailPreview"];
export type EmailPreviewRequest = Schemas["EmailPreviewRequest"];
export type EmailTestResult = Schemas["EmailTestResult"];
export type EmailSendCounts = Schemas["EmailSendCounts"];
export type Delivery = Schemas["Delivery"];
export type DeliveryPage = Schemas["DeliveryPage"];
export type DeliveryCounts = Schemas["DeliveryCounts"];
export type RetryFailedResult = Schemas["RetryFailedResult"];
export type TwoFactorChallenge = Schemas["TwoFactorChallenge"];
export type TrustedDevice = Schemas["TrustedDevice"];
export type LoginResult = Schemas["LoginResponse"] | TwoFactorChallenge;
export const isTwoFactorChallenge = (r: LoginResult): r is TwoFactorChallenge =>
  (r as TwoFactorChallenge).two_factor_required === true;
export type BatchExportFormat = "recipients" | "seating" | "pdf-zip";

/** A new draft takes a layout, or copies an existing version's. */
export type TemplateVersionCreate = { definition: TemplateDefinition } | { from_version_id: string };

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";

// Ids come from decoded URL params: encoding stops "../" escaping the segment, and "."/".." are
// refused because even %2E%2E is resolved as a dot segment by URL parsing.
const seg = (value: string) => {
  if (value === "." || value === "..") throw new Error("invalid path segment");
  return encodeURIComponent(value);
};

/**
 * ApiError carries the server's error envelope (03-api.md §3.9) rather than a bare
 * message, so callers can distinguish a 403 from a 422 and surface per-row import
 * problems without re-parsing.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: NonNullable<ErrorEnvelope["error"]["details"]> = [],
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isAuthExpired() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
}

/**
 * The access token lives in memory only. A refresh token in an httpOnly cookie is what
 * survives a reload (07.2) — keeping the access token out of localStorage means an XSS
 * bug cannot walk off with a long-lived credential.
 */
let accessToken: string | null = null;
export const setAccessToken = (token: string | null) => {
  accessToken = token;
};
export const getAccessToken = () => accessToken;

let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  // Collapse concurrent 401s into one refresh so a dashboard firing six queries at
  // once does not rotate the refresh token six times.
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) return false;
      const body = (await res.json()) as Schemas["LoginResponse"];
      setAccessToken(body.access_token);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  /** Set for mutating POSTs that trigger work (§3.1). */
  idempotencyKey?: string;
  raw?: boolean;
  /** Skip refresh-and-retry on 401: sign-in calls carry no session to renew. */
  noRefresh?: boolean;
};

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const send = async (): Promise<Response> => {
    const headers = new Headers(opts.headers);
    if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
    if (opts.idempotencyKey) headers.set("Idempotency-Key", opts.idempotencyKey);

    let body: BodyInit | undefined;
    if (opts.body instanceof FormData) {
      // Let the browser set the multipart boundary.
      body = opts.body;
    } else if (opts.body !== undefined) {
      headers.set("Content-Type", "application/json");
      body = JSON.stringify(opts.body);
    }

    return fetch(`${BASE_URL}${path}`, {
      ...opts,
      headers,
      body,
      credentials: "include",
    });
  };

  let res = await send();
  if (res.status === 401 && accessToken !== null && !opts.noRefresh) {
    if (await refreshAccessToken()) res = await send();
  }

  if (!res.ok) {
    let code = "internal_error";
    let message = res.statusText;
    let details: NonNullable<ErrorEnvelope["error"]["details"]> = [];
    try {
      const envelope = (await res.json()) as ErrorEnvelope;
      code = envelope.error.code;
      message = envelope.error.message;
      details = envelope.error.details ?? [];
    } catch {
      // Non-JSON error (proxy timeout, nginx page); keep the status text.
    }
    throw new ApiError(res.status, code, message, details);
  }

  if (opts.raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// A challenge carries no session, so the token is only set for a real LoginResponse.
export const login = async (email: string, password: string): Promise<LoginResult> => {
  const body = await api<LoginResult>("/auth/login", {
    method: "POST",
    body: { email, password },
    noRefresh: true,
  });
  if (!isTwoFactorChallenge(body)) setAccessToken(body.access_token);
  return body;
};

export const verifyTwoFactor = async (challenge: string, code: string, rememberDevice: boolean) => {
  const body = await api<Schemas["LoginResponse"]>("/auth/2fa/verify", {
    method: "POST",
    body: { challenge, code, remember_device: rememberDevice },
    noRefresh: true,
  });
  setAccessToken(body.access_token);
  return body;
};

export const resendTwoFactor = (challenge: string) =>
  api<Schemas["TwoFactorResend"]>("/auth/2fa/resend", { method: "POST", body: { challenge }, noRefresh: true });

export const listTrustedDevices = () => api<{ items: TrustedDevice[] }>("/auth/devices");

export const forgetTrustedDevice = (id: string) => api<void>(`/auth/devices/${seg(id)}`, { method: "DELETE" });

export const forgetAllTrustedDevices = () => api<void>("/auth/devices", { method: "DELETE" });

export const resetUserTwoFactor = (id: string) => api<void>(`/users/${seg(id)}/2fa/reset`, { method: "POST" });

export const logout = async () => {
  try {
    await api<void>("/auth/logout", { method: "POST" });
  } finally {
    setAccessToken(null);
  }
};

export const fetchMe = () => api<Me>("/me");

export const listBatches = (
  params: { status?: string; q?: string; limit?: number; page?: number; pageSize?: number } = {},
) => {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.q) search.set("q", params.q);
  if (params.limit) search.set("limit", String(params.limit));
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("page_size", String(params.pageSize));
  const qs = search.toString();
  return api<BatchPage>(`/batches${qs ? `?${qs}` : ""}`);
};

export const getBatch = (id: string) => api<Batch>(`/batches/${seg(id)}`);

export const previewBatch = (id: string) => api<CertificatePreview[]>(`/batches/${seg(id)}/preview`);

export const batchReport = (id: string) => api<BatchReport>(`/batches/${seg(id)}/report`);

export const approveBatch = (id: string) =>
  api<Batch>(`/batches/${seg(id)}/approve`, { method: "POST", idempotencyKey: crypto.randomUUID() });

export const rejectBatch = (id: string, comment: string) =>
  api<Batch>(`/batches/${seg(id)}/reject`, {
    method: "POST",
    body: { comment },
    idempotencyKey: crypto.randomUUID(),
  });

export const bulkBatchDecision = (body: BulkBatchDecisionRequest) =>
  api<BulkBatchDecisionResponse>("/batches/bulk-approve", {
    method: "POST",
    body,
    idempotencyKey: crypto.randomUUID(),
  });

export const retryFailedBatch = (id: string) =>
  api<{ requeued?: number }>(`/batches/${seg(id)}/retry-failed`, {
    method: "POST",
    idempotencyKey: crypto.randomUUID(),
  });

export const searchCertificates = (
  params: { q?: string; batchId?: string; limit?: number; page?: number; pageSize?: number } = {},
) => {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  if (params.batchId) search.set("batch_id", params.batchId);
  if (params.limit) search.set("limit", String(params.limit));
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("page_size", String(params.pageSize));
  const qs = search.toString();
  return api<CertificatePage>(`/certificates${qs ? `?${qs}` : ""}`);
};

export const getCertificate = (id: string) => api<CertificateDetail>(`/certificates/${seg(id)}`);

export const getCertificateVerifications = (id: string) =>
  api<CertificateVerifications>(`/certificates/${seg(id)}/verifications`);

/** The PDF needs the bearer token, so it is fetched as a blob rather than linked to. */
export const downloadCertificatePdf = async (id: string): Promise<Blob> => {
  const res = await api<Response>(`/certificates/${seg(id)}/pdf`, { raw: true });
  return res.blob();
};

export const requestRevocation = (id: string, reason: string) =>
  api<RevocationQueueItem>(`/certificates/${seg(id)}/revoke`, { method: "POST", body: { reason } });

export const getRevocation = (id: string) => api<RevocationQueueItem>(`/revocations/${seg(id)}`);

export const listRevocations = (params: { status?: string; limit?: number; page?: number; pageSize?: number } = {}) => {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.limit) search.set("limit", String(params.limit));
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("page_size", String(params.pageSize));
  const qs = search.toString();
  return api<RevocationPage>(`/revocations${qs ? `?${qs}` : ""}`);
};

export const approveRevocation = (id: string) =>
  api<RevocationQueueItem>(`/revocations/${seg(id)}/approve`, { method: "POST" });

export const rejectRevocation = (id: string, comment: string) =>
  api<RevocationQueueItem>(`/revocations/${seg(id)}/reject`, { method: "POST", body: { comment } });

export const listCertificateReissueRequests = (id: string) =>
  api<ReissueRequestList>(`/certificates/${seg(id)}/reissue-requests`);

export const requestReissue = (id: string, body: ReissueRequestCreate) =>
  api<ReissueRequest>(`/certificates/${seg(id)}/reissue-requests`, { method: "POST", body });

export const getReissueRequest = (id: string) => api<ReissueQueueItem>(`/reissue-requests/${seg(id)}`);

export const listReissueRequests = (
  params: { status?: ReissueRequest["status"]; limit?: number; page?: number; pageSize?: number } = {},
) => {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.limit) search.set("limit", String(params.limit));
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("page_size", String(params.pageSize));
  const qs = search.toString();
  return api<ReissueQueuePage>(`/reissue-requests${qs ? `?${qs}` : ""}`);
};

export const approveReissue = (id: string) =>
  api<ReissueApproveResult>(`/reissue-requests/${seg(id)}/approve`, { method: "POST" });

export const rejectReissue = (id: string, comment: string) =>
  api<ReissueRequest>(`/reissue-requests/${seg(id)}/reject`, { method: "POST", body: { comment } });

export const cancelReissue = (id: string) =>
  api<ReissueRequest>(`/reissue-requests/${seg(id)}/cancel`, { method: "POST" });

export const emailCertificate = (id: string) =>
  api<{ delivery_id: string; recipient: string }>(`/certificates/${seg(id)}/email`, { method: "POST" });

export const markCertificatePrinted = (id: string) =>
  api<{ delivery_id: string }>(`/certificates/${seg(id)}/mark-printed`, { method: "POST" });

export const retryCertificateRender = (id: string) =>
  api<{ certificate_id: string }>(`/certificates/${seg(id)}/retry-render`, { method: "POST" });

export const searchMembers = (params: { q?: string; limit?: number; page?: number; pageSize?: number } = {}) => {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  if (params.limit) search.set("limit", String(params.limit));
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("page_size", String(params.pageSize));
  const qs = search.toString();
  return api<MemberPage>(`/members${qs ? `?${qs}` : ""}`);
};

export const getMember = (id: string) => api<MemberDetail>(`/members/${seg(id)}`);

export const updateMember = (id: string, body: MemberUpdate) =>
  api<MemberDetail>(`/members/${seg(id)}`, { method: "PATCH", body });

export const importMembers = (file: File) => {
  const form = new FormData();
  form.set("file", file);
  return api<MemberImportSummary>("/members/import", { method: "POST", body: form });
};

/** The export and photo need the bearer token, so they are fetched as blobs rather than linked to. */
export const exportMembers = async (q?: string): Promise<Blob> => {
  const res = await api<Response>(`/members/export${q ? `?${new URLSearchParams({ q })}` : ""}`, { raw: true });
  return res.blob();
};

export const getMemberPhoto = async (id: string): Promise<Blob> => {
  const res = await api<Response>(`/members/${seg(id)}/photo`, { raw: true });
  return res.blob();
};

export const uploadMemberPhoto = (id: string, file: File) => {
  const form = new FormData();
  form.set("file", file);
  return api<MemberDetail>(`/members/${seg(id)}/photo`, { method: "PUT", body: form });
};

export const listPayments = (
  params: {
    status?: string;
    batchId?: string;
    memberId?: string;
    purpose?: "induction" | "reissue";
    unused?: boolean;
    limit?: number;
    page?: number; pageSize?: number;
  } = {},
) => {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.purpose) search.set("purpose", params.purpose);
  if (params.unused) search.set("unused", "true");
  if (params.batchId) search.set("batch_id", params.batchId);
  if (params.memberId) search.set("member_id", params.memberId);
  if (params.limit) search.set("limit", String(params.limit));
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("page_size", String(params.pageSize));
  const qs = search.toString();
  return api<PaymentPage>(`/payments${qs ? `?${qs}` : ""}`);
};

export const recordPayment = (body: PaymentCreate) => api<Payment>("/payments", { method: "POST", body });

export const confirmPayment = (id: string, note?: string) =>
  api<Payment>(`/payments/${seg(id)}/confirm`, { method: "POST", body: note ? { note } : undefined });

export const rejectPayment = (id: string, comment: string) =>
  api<Payment>(`/payments/${seg(id)}/reject`, { method: "POST", body: { comment } });

export const batchPaymentStatus = (id: string) => api<BatchPaymentStatus>(`/batches/${seg(id)}/payments`);

const blob = async (path: string): Promise<Blob> => {
  const res = await api<Response>(path, { raw: true });
  return res.blob();
};

export type AnalyticsQuery = { from?: string; to?: string; group?: "day" | "week" | "month"; programId?: string };

const analyticsQs = (q: AnalyticsQuery) => {
  const search = new URLSearchParams();
  if (q.from) search.set("from", q.from);
  if (q.to) search.set("to", q.to);
  if (q.group) search.set("group", q.group);
  if (q.programId) search.set("program_id", q.programId);
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

export const getAnalyticsSummary = (q: AnalyticsQuery) => api<AnalyticsSummary>(`/analytics/summary${analyticsQs(q)}`);

export const exportAnalytics = (q: AnalyticsQuery): Promise<Blob> => blob(`/analytics/export${analyticsQs(q)}`);

export const listPrograms = () => api<Program[]>("/programs");

export const getProgram = (id: string) => api<Program>(`/programs/${seg(id)}`);

export const createProgram = (body: ProgramCreate) => api<Program>("/programs", { method: "POST", body });

export type ProgramSignatory = Schemas["ProgramSignatory"];
export const listProgramSignatories = (id: string) => api<ProgramSignatory[]>(`/programs/${seg(id)}/signatories`);
export const setProgramSignatories = (id: string, slots: Record<string, string>) =>
  api<ProgramSignatory[]>(`/programs/${seg(id)}/signatories`, { method: "PUT", body: { slots } });

export const updateProgram = (id: string, body: ProgramUpdate) =>
  api<Program>(`/programs/${seg(id)}`, { method: "PATCH", body });

export const listTemplates = () => api<Template[]>("/templates");

export const createTemplate = (body: { name: string; cert_type: string }) =>
  api<Template>("/templates", { method: "POST", body });

export const getTemplate = (id: string) => api<TemplateDetail>(`/templates/${seg(id)}`);

export const getTemplateVersion = (id: string, versionId: string) =>
  api<TemplateVersionDetail>(`/templates/${seg(id)}/versions/${seg(versionId)}`);

export const createTemplateVersion = (id: string, body: TemplateVersionCreate) =>
  api<TemplateVersion>(`/templates/${seg(id)}/versions`, { method: "POST", body });

/** A 422 carries each problem in `details`, with `field` as its path in the definition. */
export const updateTemplateVersion = (id: string, versionId: string, definition: TemplateDefinition) =>
  api<TemplateVersion>(`/templates/${seg(id)}/versions/${seg(versionId)}`, { method: "PUT", body: { definition } });

export const publishTemplateVersion = (id: string, versionId: string) =>
  api<TemplateVersion>(`/templates/${seg(id)}/versions/${seg(versionId)}/publish`, { method: "POST" });

/** Preview, font, asset and signature files need the bearer token, so they are fetched as blobs. */
export const previewTemplateVersion = (
  id: string,
  versionId: string,
  params: { signatureId?: string; batchId?: string } = {},
) => {
  const search = new URLSearchParams();
  if (params.signatureId) search.set("signature_id", params.signatureId);
  if (params.batchId) search.set("batch_id", params.batchId);
  const qs = search.toString();
  return blob(`/templates/${seg(id)}/versions/${seg(versionId)}/preview${qs ? `?${qs}` : ""}`);
};

export const listFonts = () => api<Font[]>("/fonts");

export const uploadFont = (file: File, family: string, style?: FontStyle) => {
  const form = new FormData();
  form.set("file", file);
  form.set("family", family);
  if (style) form.set("style", style);
  return api<Font>("/fonts", { method: "POST", body: form });
};

export const getFontFile = (id: string) => blob(`/fonts/${seg(id)}/file`);

export const listTemplateAssets = () => api<TemplateAsset[]>("/template-assets");

export const uploadTemplateAsset = (file: File, name?: string) => {
  const form = new FormData();
  form.set("file", file);
  if (name) form.set("name", name);
  return api<TemplateAsset>("/template-assets", { method: "POST", body: form });
};

export const getTemplateAssetFile = (id: string) => blob(`/template-assets/${seg(id)}/file`);

export const listSignatures = () => api<Signature[]>("/signatures");

export const createSignature = (body: { signatory_name: string; role_title?: string }) =>
  api<Signature>("/signatures", { method: "POST", body });

export const listSignatureVersions = (id: string) => api<SignatureVersion[]>(`/signatures/${seg(id)}/versions`);

export const createSignatureVersion = (id: string, image: File, effectiveFrom: string, effectiveTo?: string) => {
  const form = new FormData();
  form.set("image", image);
  form.set("effective_from", effectiveFrom);
  if (effectiveTo) form.set("effective_to", effectiveTo);
  return api<SignatureVersion>(`/signatures/${seg(id)}/versions`, { method: "POST", body: form });
};

export const getSignatureImage = (id: string, versionId: string) =>
  blob(`/signatures/${seg(id)}/versions/${seg(versionId)}/image`);

export const listPrinterProfiles = (params: { includeArchived?: boolean } = {}) =>
  api<PrinterProfile[]>(`/printer-profiles${params.includeArchived ? "?include_archived=true" : ""}`);

export const getPrinterProfile = (id: string) => api<PrinterProfile>(`/printer-profiles/${seg(id)}`);

export const createPrinterProfile = (body: PrinterProfileCreate) =>
  api<PrinterProfile>("/printer-profiles", { method: "POST", body });

/** Send only what changes; `null` clears a nullable key, so a margin goes back to the template's. */
export const updatePrinterProfile = (id: string, body: PrinterProfileUpdate) =>
  api<PrinterProfile>(`/printer-profiles/${seg(id)}`, { method: "PATCH", body });

export const archivePrinterProfile = (id: string) =>
  api<PrinterProfile>(`/printer-profiles/${seg(id)}/archive`, { method: "POST" });

export const testPrinterProfile = (id: string) =>
  api<PrinterTestResult>(`/printer-profiles/${seg(id)}/test`, { method: "POST" });

/** Alignment pages, spools and exports need the bearer token, so they are fetched as blobs. */
export const getAlignmentPage = (id: string, templateVersionId: string) =>
  blob(`/printer-profiles/${seg(id)}/alignment?${new URLSearchParams({ template_version_id: templateVersionId })}`);

/** `replayed` is true when the key matched an earlier request: nothing new was created. */
export const distributePrint = async (batchId: string, body: DistributePrintRequest, idempotencyKey: string) => {
  const res = await api<Response>(`/batches/${seg(batchId)}/distribute/print`, { method: "POST", body, idempotencyKey, raw: true });
  const created = (await res.json()) as PrintRunCreated;
  return { ...created, replayed: res.headers.get("Idempotent-Replayed") === "true" };
};

export const listPrintRuns = (batchId: string) => api<PrintRun[]>(`/batches/${seg(batchId)}/print-runs`);

export const downloadPrintSpool = (id: string) => blob(`/print-spools/${seg(id)}/file`);

export const sendPrintSpool = (id: string) => api<PrintSpool>(`/print-spools/${seg(id)}/send`, { method: "POST" });

export const retryPrintSpool = (id: string) => api<PrintSpool>(`/print-spools/${seg(id)}/retry`, { method: "POST" });

export const recordSpoolOutcome = (id: string, outcome: SpoolOutcome, note?: string) =>
  api<PrintSpool>(`/print-spools/${seg(id)}/outcome`, { method: "POST", body: note ? { outcome, note } : { outcome } });

export const listEmailTemplates = (params: { includeArchived?: boolean } = {}) =>
  api<EmailTemplate[]>(`/email-templates${params.includeArchived ? "?archived=true" : ""}`);

export const getEmailTemplate = (id: string) => api<EmailTemplate>(`/email-templates/${seg(id)}`);

export const createEmailTemplate = (body: EmailTemplateCreate) =>
  api<EmailTemplate>("/email-templates", { method: "POST", body });

/** Send only what changes; `is_default` accepts only true. */
export const updateEmailTemplate = (id: string, body: EmailTemplateUpdate) =>
  api<EmailTemplate>(`/email-templates/${seg(id)}`, { method: "PATCH", body });

export const archiveEmailTemplate = (id: string) =>
  api<EmailTemplate>(`/email-templates/${seg(id)}`, { method: "DELETE" });

/** Unsaved `subject` and `body_text` are previewed instead of the stored ones. */
export const previewEmailTemplate = (id: string, body: EmailPreviewRequest) =>
  api<EmailPreview>(`/email-templates/${seg(id)}/preview`, { method: "POST", body });

export const sendTestEmail = (id: string, body?: { certificate_id?: string }) =>
  api<EmailTestResult>(`/email-templates/${seg(id)}/test`, { method: "POST", body });

/** A dry run: counts what a send would queue, and queues nothing. */
export const previewEmailSend = (batchId: string, emailTemplateId: string) =>
  api<EmailSendCounts>(`/batches/${seg(batchId)}/distribute/email?dry_run=true`, {
    method: "POST",
    body: { email_template_id: emailTemplateId },
  });

/** `replayed` is true when the key matched an earlier send: nothing new was queued. */
export const distributeEmail = async (batchId: string, emailTemplateId: string, idempotencyKey: string) => {
  const res = await api<Response>(`/batches/${seg(batchId)}/distribute/email`, {
    method: "POST",
    body: { email_template_id: emailTemplateId },
    idempotencyKey,
    raw: true,
  });
  const counts = (await res.json()) as EmailSendCounts;
  return { ...counts, replayed: res.headers.get("Idempotent-Replayed") === "true" };
};

export const listDeliveries = (
  params: {
    batchId?: string;
    channel?: "email" | "print";
    status?: Delivery["status"];
    q?: string;
    limit?: number;
    page?: number; pageSize?: number;
  } = {},
) => {
  const search = new URLSearchParams();
  if (params.batchId) search.set("batch_id", params.batchId);
  if (params.channel) search.set("channel", params.channel);
  if (params.status) search.set("status", params.status);
  if (params.q) search.set("q", params.q);
  if (params.limit) search.set("limit", String(params.limit));
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("page_size", String(params.pageSize));
  const qs = search.toString();
  return api<DeliveryPage>(`/deliveries${qs ? `?${qs}` : ""}`);
};

export const retryDelivery = (id: string) => api<Delivery>(`/deliveries/${seg(id)}/retry`, { method: "POST" });

export const retryFailedEmails = (batchId: string) =>
  api<RetryFailedResult>(`/batches/${seg(batchId)}/deliveries/retry-failed`, { method: "POST" });

export const resendDelivery = (id: string) => api<Delivery>(`/deliveries/${seg(id)}/resend`, { method: "POST" });

export const exportBatch = (batchId: string, format: BatchExportFormat) =>
  blob(`/batches/${seg(batchId)}/export?${new URLSearchParams({ format })}`);

export const listUsers = (
  params: { q?: string; roleId?: string; active?: boolean; limit?: number; page?: number; pageSize?: number } = {},
) => {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  if (params.roleId) search.set("role_id", params.roleId);
  if (params.active !== undefined) search.set("active", String(params.active));
  if (params.limit) search.set("limit", String(params.limit));
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("page_size", String(params.pageSize));
  const qs = search.toString();
  return api<UserPage>(`/users${qs ? `?${qs}` : ""}`);
};

export const getUser = (id: string) => api<User>(`/users/${seg(id)}`);

export const createUser = (body: UserCreate) => api<User>("/users", { method: "POST", body });

/** A 409 is the lockout guard or self-deactivation; its message says which. */
export const updateUser = (id: string, body: UserUpdate) => api<User>(`/users/${seg(id)}`, { method: "PATCH", body });

/** Ends every earlier session of that user, the caller's own included when it is them. */
export const setUserPassword = (id: string, password: string) =>
  api<void>(`/users/${seg(id)}/password`, { method: "PUT", body: { password } });

export const listRoles = () => api<Role[]>("/roles");

export const getRole = (id: string) => api<Role>(`/roles/${seg(id)}`);

export const createRole = (body: RoleCreate) => api<Role>("/roles", { method: "POST", body });

export const updateRole = (id: string, body: RoleUpdate) => api<Role>(`/roles/${seg(id)}`, { method: "PATCH", body });

export const deleteRole = (id: string) => api<void>(`/roles/${seg(id)}`, { method: "DELETE" });

export const setRolePermissions = (id: string, permissions: string[]) =>
  api<Role>(`/roles/${seg(id)}/permissions`, { method: "PUT", body: { permissions } });

export const listPermissions = () => api<Permission[]>("/permissions");

export const searchAudit = (
  params: {
    action?: string;
    entityType?: string;
    entityId?: string;
    actorUserId?: string;
    from?: string;
    to?: string;
    limit?: number;
    page?: number; pageSize?: number;
  } = {},
) => {
  const search = new URLSearchParams();
  if (params.action) search.set("action", params.action);
  if (params.entityType) search.set("entity_type", params.entityType);
  if (params.entityId) search.set("entity_id", params.entityId);
  if (params.actorUserId) search.set("actor_user_id", params.actorUserId);
  if (params.from) search.set("from", params.from);
  if (params.to) search.set("to", params.to);
  if (params.limit) search.set("limit", String(params.limit));
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("page_size", String(params.pageSize));
  const qs = search.toString();
  return api<AuditPage>(`/audit${qs ? `?${qs}` : ""}`);
};

/** Restores a session on page load from the refresh cookie. */
export const restoreSession = async (): Promise<Me | null> => {
  if (!(await refreshAccessToken())) return null;
  try {
    return await fetchMe();
  } catch {
    return null;
  }
};
